import React, { useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { DropdownSingle, TextInput, colors2, UI_BODY_SM_DF, DateRangePicker } from "oolib";
import { ButtonPrimary, ButtonSecondary } from "oolib/dist/v2/components/Buttons";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
  ConnectedBanner,
  ConnectedMeta,
  StaleBanner,
  ErrorBanner,
  EmptyBanner,
} from "./BaseIntegrationInterface";
import { useOAuthConnection } from "../../hooks/useOAuthConnection";
import { IntegrationDisconnectConfirmModal } from "@/_self_managed/SM_Integrations/IntegrationDisconnectConfirmModal";

// Zendesk uses OAuth with refresh tokens (fits the framework cleanly), with
// one quirk: every URL is namespaced under <subdomain>.zendesk.com. The FE
// collects the subdomain in a TextInput BEFORE the OAuth redirect; we ship
// it as providerParams.subdomain and the BE round-trips it through OAuth
// state. providerAccountId is stored as the bare subdomain (single value,
// no compositing — simpler than Zoho Desk's "<orgId>@<region>").

// ============================================
// ZENDESK ICON
// ============================================

export const ZendeskIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#03363D" />
    <path
      fill="#FFFFFF"
      d="M5 11h10L5 24V11zm12 13c0-2.8 2.2-5 5-5s5 2.2 5 5H17zM17 8h10L17 21V8zM5 24c0-2.8 2.2-5 5-5s5 2.2 5 5H5z"
    />
  </svg>
);

// ============================================
// CONSTANTS
// ============================================

const STATUS_OPTIONS = [
  { display: "All",      value: "all" },
  { display: "New",      value: "new" },
  { display: "Open",     value: "open" },
  { display: "Pending",  value: "pending" },
  { display: "On Hold",  value: "hold" },
  { display: "Solved",   value: "solved" },
  { display: "Closed",   value: "closed" },
];

const DAY_MS = 24 * 60 * 60 * 1000;
const SOFT_WARN_DAYS = 90;
const HARD_CAP_DAYS = 365;

// Subdomain shape mirrors providers/zendesk.js SUBDOMAIN_REGEX.
const SUBDOMAIN_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

const isoDaysAgo = (n) => new Date(Date.now() - n * DAY_MS).toISOString().slice(0, 10);
const todayIso = () => new Date().toISOString().slice(0, 10);

// ============================================
// STYLED COMPONENTS
// ============================================

const FieldGroup = styled.div`
  margin-top: 16px;
`;

const DateRangeRow = styled.div`
  margin-top: 16px;
`;

const RangeHint = styled(UI_BODY_SM_DF)`
  color: ${({ $error }) => ($error ? "#c0392b" : colors2.grey60)};
  margin-top: 8px;
`;

// ============================================
// INTERFACE
// ============================================

export const ZendeskInterface = ({
  adapterConfig,
  onSubmit,
  isLoading,
  submitError,
  onClearSubmitError,
}) => {
  const {
    connection,
    connectionLoading,
    connectError,
    handleConnect,
    isConnecting,
    handleDisconnect,
  } = useOAuthConnection({
    providerId: "zendesk",
    providerName: "Zendesk",
    preflightEnabled: false,
  });

  // Pre-connect input: customer's Zendesk subdomain.
  const [subdomain, setSubdomain] = useState("");

  // Import filters (connected-state).
  const [dateRange, setDateRange] = useState([isoDaysAgo(30), todayIso()]);
  const [startDate, endDate] = dateRange;
  const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[0]);

  const submitErrorMessage =
    submitError && submitError.sourceId === "zendesk" ? submitError.message : null;
  const isEmptyResult =
    submitErrorMessage && /no\s+(zendesk\s+)?(tickets|data)\s+(found|to)/i.test(submitErrorMessage);

  const rangeDays = startDate && endDate
    ? Math.round((new Date(endDate) - new Date(startDate)) / DAY_MS)
    : 0;
  const rangeTooLong = rangeDays > HARD_CAP_DAYS;
  const rangeWarn = !rangeTooLong && rangeDays > SOFT_WARN_DAYS;
  const rangeInverted = startDate && endDate && new Date(startDate) > new Date(endDate);

  const subdomainTrimmed = subdomain.trim().toLowerCase();
  const subdomainValid = SUBDOMAIN_REGEX.test(subdomainTrimmed);

  const canSubmit = connection?.connected
    && connection?.status !== "stale"
    && startDate && endDate
    && !rangeTooLong
    && !rangeInverted;

  // Accept either a bare subdomain (`acme`) or a full host (`acme.zendesk.com`).
  // The BE expects the bare label.
  const normaliseSubdomain = (raw) => {
    if (!raw) return "";
    let s = String(raw).trim().toLowerCase();
    // strip protocol + path if user pasted the whole URL
    s = s.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    // strip .zendesk.com suffix if present
    s = s.replace(/\.zendesk\.com$/, "");
    return s;
  };

  const onConnectClick = (overrideSubdomain) => {
    const raw = overrideSubdomain != null ? overrideSubdomain : subdomain;
    const cleaned = normaliseSubdomain(raw);
    if (!SUBDOMAIN_REGEX.test(cleaned)) return;
    handleConnect({ providerParams: { subdomain: cleaned } });
  };

  const onReconnectClick = () => {
    // Reuse the stored subdomain — user doesn't have to retype.
    const stored = connection?.providerAccountId;
    if (stored) onConnectClick(stored);
  };

  const onDisconnect = async () => {
    await handleDisconnect();
    setSubdomain("");
  };

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await onDisconnect();
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onClearSubmitError && onClearSubmitError();
    // Anchor to local-day boundaries (same TZ fix from Notion / Zoho Desk).
    const createdAfter = moment(startDate).startOf("day").toISOString();
    const createdBefore = moment(endDate).endOf("day").toISOString();
    onSubmit({
      adapterOptions: {
        startDate: createdAfter,
        endDate: createdBefore,
        statusFilter: statusFilter?.value || "all",
      },
    });
  };

  // ------- Render -------

  if (connectionLoading) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Zendesk</InterfaceHeader>
        <InterfaceDescription>Checking connection status…</InterfaceDescription>
      </InterfaceContainer>
    );
  }

  // State 1: not connected — collect subdomain.
  if (!connection?.connected) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Zendesk</InterfaceHeader>
        <InterfaceDescription>
          Import support tickets from Zendesk. Enter your Zendesk subdomain
          below, then authorize Dots to read tickets from your account.
        </InterfaceDescription>

        <FieldGroup>
          <TextInput
            label="Your Zendesk subdomain"
            placeholder="acme  (or acme.zendesk.com)"
            value={subdomain}
            onChange={(k, v) => setSubdomain(v)}
          />
          {subdomain && !subdomainValid && (
            <RangeHint $error>
              Expected just the subdomain part (e.g. <strong>acme</strong>) —
              lowercase letters, digits, hyphens.
            </RangeHint>
          )}
        </FieldGroup>

        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}

        <InterfaceActions>
          <ButtonPrimary onClick={() => onConnectClick()} disabled={!subdomainValid || isConnecting} isLoading={isConnecting}>
            Connect Zendesk
          </ButtonPrimary>
        </InterfaceActions>

        <HelpText>
          Your subdomain is the part before <strong>.zendesk.com</strong> in
          your help-desk URL. Dots requests broad read access (tickets, users,
          organizations) — your admin can review the scope on Zendesk's consent
          screen.
        </HelpText>
      </InterfaceContainer>
    );
  }

  // State 3: stale — reconnect with the same subdomain (no retype).
  if (connection.status === "stale") {
    const stored = connection.providerAccountId;
    return (
      <InterfaceContainer>
        <InterfaceHeader>Reconnect Zendesk</InterfaceHeader>
        <StaleBanner>
          <ConnectedMeta>
            <strong>⚠ Connection needs to be renewed</strong>
            <span>
              {stored
                ? `Your Zendesk connection (${stored}.zendesk.com) expired or was revoked.`
                : "Your Zendesk connection expired or was revoked."}
            </span>
          </ConnectedMeta>
          <ButtonPrimary onClick={onReconnectClick} disabled={!stored || isConnecting} isLoading={isConnecting}>
            Reconnect Zendesk
          </ButtonPrimary>
        </StaleBanner>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      </InterfaceContainer>
    );
  }

  // State 2: connected & ready to import.
  const storedSubdomain = connection.providerAccountId;

  return (
    <InterfaceContainer>
      <InterfaceHeader>Import from Zendesk</InterfaceHeader>
      <InterfaceDescription>
        Each ticket becomes one row. The comment thread (public + internal)
        is rendered as a single transcript column.
      </InterfaceDescription>

      <ConnectedBanner>
        <ConnectedMeta>
          <strong>
            ✓ Connected{connection.providerAccountName ? ` to ${connection.providerAccountName}` : ""}
          </strong>
          {storedSubdomain && (
            <span style={{ color: colors2.grey60 }}>{storedSubdomain}.zendesk.com</span>
          )}
          {connection.adminEmail && (
            <span style={{ color: colors2.grey60 }}>{connection.adminEmail}</span>
          )}
        </ConnectedMeta>
        <ButtonSecondary onClick={() => setShowDisconnectConfirm(true)} disabled={isLoading}>
          Disconnect
        </ButtonSecondary>
      </ConnectedBanner>

      {showDisconnectConfirm && (
        <IntegrationDisconnectConfirmModal
          name="Zendesk"
          onConfirm={confirmDisconnect}
          onClose={() => setShowDisconnectConfirm(false)}
          isLoading={isDisconnecting}
        />
      )}

      {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      {submitErrorMessage && isEmptyResult && <EmptyBanner>{submitErrorMessage}</EmptyBanner>}
      {submitErrorMessage && !isEmptyResult && <ErrorBanner>{submitErrorMessage}</ErrorBanner>}

      <DateRangeRow>
        <DateRangePicker
          id="zendesk-date-range"
          label="Date Range"
          value={dateRange}
          onChange={(k, v) => setDateRange(v)}
          previousDateOnly
          readOnly={isLoading}
        />
      </DateRangeRow>

      {rangeInverted && (
        <RangeHint $error>End date must be on or after start date.</RangeHint>
      )}
      {rangeTooLong && (
        <RangeHint $error>
          Please select a range of {HARD_CAP_DAYS} days or fewer.
        </RangeHint>
      )}
      {rangeWarn && (
        <RangeHint>
          Large date ranges ({rangeDays} days) may take several minutes to import.
        </RangeHint>
      )}

      <FieldGroup>
        <DropdownSingle
          label="Ticket Status"
          placeholder="Select status..."
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(k, v) => setStatusFilter(v)}
          readOnly={isLoading}
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
      </FieldGroup>

      <RangeHint>
        Heads up: imports are capped at the most recent 200 tickets in the
        selected range for now. Narrow the date range or run multiple imports
        if you need more.
      </RangeHint>

      <InterfaceActions>
        <ButtonPrimary
          onClick={handleSubmit}
          disabled={!canSubmit || isLoading}
          isLoading={isLoading}
        >
          {adapterConfig?.preflightConfig?.submitLabel || "Fetch Tickets"}
        </ButtonPrimary>
      </InterfaceActions>
    </InterfaceContainer>
  );
};

export const zendeskIntegrationMeta = {
  id: "zendesk",
  name: "Zendesk",
  description: "Import support tickets from Zendesk",
};

export default ZendeskInterface;
